import { useI18n } from '@/hooks/useI18n';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Pause } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import type { AccountResponse } from '@/types/api';
import { formatRelative } from '@/lib/format';

interface HealthListProps {
  accounts: AccountResponse[] | undefined;
  loading?: boolean;
}

export function HealthList({ accounts, loading }: HealthListProps) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const list = accounts ?? [];

  return (
    <section className="space-y-2">
      <header className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[14px] font-semibold tracking-tight text-text-1">
          {t('Santé des comptes', 'Account health')}
        </h3>
        <span className="text-[11px] text-text-3">
          {loading ? 'scan…' : t(`${list.length} à surveiller`, `${list.length} to watch`)}
        </span>
      </header>
      <div className="overflow-hidden rounded-[14px] border border-line-1 bg-bg-1 shadow-card">
        {loading && (
          <div className="space-y-px p-0.5">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full rounded-none" />
            ))}
          </div>
        )}
        {!loading && list.length === 0 && (
          <div className="px-5 py-6 text-center font-mono text-[11px] text-text-3">
            {t('// all clear · aucun compte à surveiller', '// all clear · no accounts to watch')}
          </div>
        )}
        {!loading && list.length > 0 && (
          <ul className="divide-y divide-line-1/70">
            {list.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => navigate(`/accounts/${a.id}`)}
                  className="group grid min-h-11 w-full grid-cols-[28px_minmax(0,1fr)] items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left transition-colors hover:bg-bg-2/70 sm:flex sm:flex-wrap"
                >
                  <Avatar className="row-span-2 h-7 w-7 shrink-0">
                    <AvatarFallback className="bg-violet-soft text-[10px] font-semibold text-violet-deep">
                      {a.username.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-1 transition-colors group-hover:text-violet-deep">
                    @{a.username}
                  </span>
                  <span className="col-start-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 sm:contents">
                    {a.pausedReason && (
                      <span className="flex min-w-0 items-center gap-1 break-all text-[11px] text-amber">
                        <Pause className="h-3 w-3 shrink-0" />
                        {a.pausedReason}
                      </span>
                    )}
                    {a.consecutiveErrors > 0 && (
                      <span className="flex items-center gap-1 text-[11px] text-rose">
                        <AlertTriangle className="h-3 w-3 shrink-0" />
                        {a.consecutiveErrors}
                      </span>
                    )}
                    <span className="text-[11px] text-text-3">
                      {formatRelative(a.lastCheckedAt)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
